import * as v from 'valibot'
import { COMPACT_ANSWERS_PATTERN, encodeAnswers } from 'shared/public-answers'

// Valibot duplicate of shared's `Profile`, so validateSearch doesn't pull the zod runtime into the entry chunk.
// Keep in sync with `Profile`, `Filters`, `SiteInferProfile`, `TeamProfileEvaluation` and `Site` in apps/shared/src/shared.ts.

const Site = v.picklist([
  'universal',
  'linkedin',
  'infojobs',
  'buscojobs',
  'manpower',
  'randstad',
  'tecnoempleo',
  'talentclue',
])

const Sex = v.picklist(['male', 'female', 'neutral'])

// Exported so `collectiveFilters` picks its subset from here rather than re-typing the bounds a third time.
export const FiltersShape = {
  sex: v.fallback(v.optional(v.array(Sex)), undefined),
  ageMin: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  ageMax: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  peopleInCharge: v.optional(v.array(v.boolean())),
  level: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(5)))),
  function: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(34)))),
  sector: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(147)))),
  employees: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(8)))),
  educationalLevel: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(6)))),
  country: v.optional(v.array(v.string())),
  ids: v.optional(v.array(v.number())),
  filtersOrder: v.optional(v.array(v.string())),
} as const

const SiteInferProfileShape = {
  site: v.fallback(v.optional(Site), undefined),
  name: v.nullish(v.string()),
  levelText: v.optional(v.string()),
  functionText: v.optional(v.string()),
  sectorText: v.optional(v.string()),
  ids: v.optional(v.array(v.number())),
} as const

const TeamProfileEvaluationShape = {
  teamId: v.optional(v.number()),
  evaluation: v.fallback(v.optional(v.picklist(['owns', 'ownsManual', 'team', 'manual'])), undefined),
  members: v.optional(v.array(v.number())),
  name: v.nullish(v.string()),
} as const

/**
 * Mirrors `answerSchema` / `answersSchema` from `shared.ts`: 36 answers, each a permutation of [0, 1, 2].
 * Three digits plus one distinctness check, rather than a union of the six permutations. The union cost
 * 12.3% of server CPU on `/report`: valibot tries arms in order, and every failed arm allocated an issue
 * and stringified the received value — ~380 discarded issue objects per pass.
 * Still a `v.tuple`, which ACCEPTS extra items and drops them, so a `[0, 1, 2, 0]` answer normalises and
 * renders rather than 500ing. Fuzzed over 4,000 malformed inputs; 5.7x faster (22.16µs → 3.89µs).
 */
const answerDigit = v.picklist([0, 1, 2])

const answerSchema = v.pipe(
  v.tuple([answerDigit, answerDigit, answerDigit]),
  v.check(
    (answer) => answer[0] !== answer[1] && answer[1] !== answer[2] && answer[0] !== answer[2],
    'Expected a permutation of [0, 1, 2]',
  ),
)

// Exactly 36 answers, but tolerant of a longer list, the way the 36-element tuple it mirrors is: a
// `v.length(36)` would turn a 37-answer URL into a 500, so require at least 36 and then trim. An array
// rather than a tuple because the zod mirror in shared.ts is one, and the two MUST infer the same type
// or the router's search types stop matching the generated route tree (TS2719).
const answersSchema = v.pipe(
  v.array(answerSchema),
  v.minLength(36),
  v.transform((answers) => (answers.length === 36 ? answers : answers.slice(0, 36))),
)

/**
 * What a PUBLIC profile's answers are IN SEARCH STATE: the compact 36-character string from
 * `shared/public-answers` (one seroval node), never the 108 tuples. The compact form passes through unchanged;
 * the legacy tuple form — a link shared before the compact encoding — is accepted and normalised here, so the
 * router's `search`, the `getReport` query key and its `queryHash` all carry the string whichever form the URL
 * had. The server's zod twin (`publicAnswersSchema`) accepts both too and decodes back to tuples for the report
 * computation. Compact arm first: it is the common case, and an array fails `v.string` with one cheap issue.
 */
const publicAnswersSchema = v.union([
  v.pipe(v.string(), v.regex(COMPACT_ANSWERS_PATTERN)),
  v.pipe(
    answersSchema,
    v.transform((answers) => encodeAnswers(answers)),
  ),
])

export const Profile = v.variant('type', [
  v.object({ type: v.literal('employee'), id: v.number() }),
  v.object({
    type: v.literal('market_company'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_company_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('applicant'), id: v.optional(v.number()) }),
  v.object({
    type: v.literal('job'),
    id: v.number(),
    source: v.fallback(v.optional(v.picklist(['described', 'integrants'])), undefined),
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('team'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('member'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('org_group'), id: v.string() }),
  v.object({ type: v.literal('org_user'), id: v.string() }),
  v.object({
    type: v.literal('market'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_applicant'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('public'),
    id: v.optional(v.number()),
    name: v.optional(v.string()),
    competences: v.optional(publicAnswersSchema),
    values: v.optional(publicAnswersSchema),
    personality: v.optional(publicAnswersSchema),
  }),
])

export type Profile = v.InferOutput<typeof Profile>
